// Form1.jsx (no auto-save)
import { useState, useRef, useEffect, useCallback } from "react";
import styles from "./Form1.module.scss";
import indianFlag from "../../../assets/images/indian-flag.svg";
import saveIcon from "../../../assets/images/save-icon.svg";
import { Getcanleaddata, getJobRole, GetPreferredCities, GetPrefferedAreaList, PostRegister } from "../../../apiServices";
import { decryptedAdminID, calculateAge } from "../../../utility";

const GENDER_OPTIONS = ["Male", "Female", "Other"];

function MultiCitySelect({ options, placeholder, selectedCities, onSelect, onRemove, isError, disabled, loading }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  const availableOptions = options.filter(
    (city) => !selectedCities.some(c => c.id === city.id) &&
      city.city.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (city) => {
    onSelect(city);
    setSearch("");
    setOpen(false);
  };

  return (
    <div className={styles.multiSelectWrapper} ref={wrapRef}>
      <div className={styles.selectedItems}>
        {selectedCities.map((city) => (
          <span key={city.id} className={styles.selectedTag}>
            {city.city}
            <button type="button" className={styles.removeTag} onClick={() => onRemove(city.id)} disabled={disabled}>×</button>
          </span>
        ))}
        <div className={styles.inputWrapper}>
          <input
            type="text"
            placeholder={selectedCities.length === 0 ? placeholder : ""}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            disabled={disabled}
            className={styles.skillInput}
          />
          <button type="button" className={styles.dropdownBtn} onClick={() => !disabled && setOpen(!open)} disabled={disabled}>
            {open ? "▴" : "▾"}
          </button>
        </div>
      </div>
      {open && (
        <div className={styles.dropdown}>
          <ul className={styles.optionList}>
            {loading ? (
              <li className={styles.noOption}>Loading cities...</li>
            ) : availableOptions.length > 0 ? (
              availableOptions.map((city) => (
                <li key={city.id} className={styles.optionItem} onClick={() => handleSelect(city)}>
                  {city.city}
                </li>
              ))
            ) : (
              <li className={styles.noOption}>No cities found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function MultiAreaSelect({ options, placeholder, selectedAreas, onSelect, onRemove, isError, disabled, loading }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  const safeSelectedAreas = Array.isArray(selectedAreas) ? selectedAreas : [];
  const availableOptions = (options || []).filter(
    (area) => area && !safeSelectedAreas.includes(area) &&
      area.toLowerCase().includes((search || "").toLowerCase())
  );

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (area) => {
    if (onSelect && area) onSelect(area);
    setSearch("");
    setOpen(false);
  };

  return (
    <div className={styles.multiSelectWrapper} ref={wrapRef}>
      <div className={styles.selectedItems}>
        {safeSelectedAreas.map((area, index) => (
          <span key={index} className={styles.selectedTag}>
            {area}
            <button type="button" className={styles.removeTag} onClick={() => onRemove && onRemove(area)} disabled={disabled}>×</button>
          </span>
        ))}
        <div className={styles.inputWrapper}>
          <input
            type="text"
            placeholder={safeSelectedAreas.length === 0 ? placeholder : ""}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            disabled={disabled}
            className={styles.skillInput}
          />
          <button type="button" className={styles.dropdownBtn} onClick={() => !disabled && setOpen(!open)} disabled={disabled}>
            {open ? "▴" : "▾"}
          </button>
        </div>
      </div>
      {open && (
        <div className={styles.dropdown}>
          <ul className={styles.optionList}>
            {loading ? (
              <li className={styles.noOption}>Loading areas...</li>
            ) : availableOptions.length > 0 ? (
              availableOptions.map((area, index) => (
                <li key={index} className={styles.optionItem} onClick={() => handleSelect(area)}>
                  {area}
                </li>
              ))
            ) : (
              <li className={styles.noOption}>No areas found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function AutoSelect({ options, placeholder, value, onChange, isError, disabled, loading }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  const filtered = options.filter((o) => o.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (opt) => {
    onChange(opt);
    setOpen(false);
    setSearch("");
  };

  const handleOpen = () => {
    if (!disabled) {
      setOpen((prev) => !prev);
      setSearch("");
    }
  };

  return (
    <div className={`${styles.autoSelectWrap}${isError ? " " + styles.err : ""}${disabled ? " " + styles.disabled : ""}`} ref={wrapRef}>
      <div className={styles.autoSelectTrigger} onClick={handleOpen}>
        <span className={value ? styles.selectedVal : styles.placeholderVal}>{value || placeholder}</span>
        <span className={styles.arrow}>{open ? "▴" : "▾"}</span>
      </div>
      {open && (
        <div className={styles.dropdown}>
          <div className={styles.searchWrap}>
            <input autoFocus className={styles.searchInput} placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} onClick={(e) => e.stopPropagation()} />
          </div>
          <ul className={styles.optionList}>
            {loading ? (
              <li className={styles.noOption}>Loading...</li>
            ) : filtered.length > 0 ? (
              filtered.map((opt) => (
                <li key={opt} className={`${styles.optionItem}${value === opt ? " " + styles.active : ""}`} onClick={() => handleSelect(opt)}>
                  {opt}
                </li>
              ))
            ) : (
              <li className={styles.noOption}>No options found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function PhonePrefix() {
  return (
    <div className={styles.phonePrefix}>
      <span className={styles.flagIcon}><img src={indianFlag} alt="Indian Flag" width="20" height="14" /></span>
      <span className={styles.dialCode}>+91</span>
      <span className={styles.prefixDivider} />
    </div>
  );
}

// ── Phone Input with India Flag ───────────────────────────────────────────────
function PhoneInput({ name, placeholder, value, onChange, isError, disabled = false, }) {
  return (
    <div className={`${styles.phoneWrap} ${isError ? styles.inputError : ""}`}>
      <PhonePrefix />
      <input
        name={name}
        placeholder={placeholder}
        value={value || ""}
        maxLength={10}
        inputMode="numeric"
        onChange={onChange}
        onInput={(e) => {
          e.target.value = e.target.value.replace(/[^0-9]/g, "");
        }}
        disabled={disabled}
      />
    </div>
  );
}

function DateInput({ name, value, onChange, isError }) {
  const [displayValue, setDisplayValue] = useState("");
  const dateInputRef = useRef(null);

  // Format YYYY-MM-DD to DD/MM/YYYY for display
  const formatToDisplay = (dateValue) => {
    if (!dateValue) return "";
    const [year, month, day] = dateValue.split("-");
    if (!year || !month || !day) return "";
    return `${day}/${month}/${year}`;
  };

  // Format DD/MM/YYYY to YYYY-MM-DD for storage
  const formatToStorage = (displayDate) => {
    if (!displayDate) return "";
    const parts = displayDate.split("/");
    if (parts.length !== 3) return "";
    const [day, month, year] = parts;
    if (!day || !month || !year) return "";
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };

  // Validate date
  const isValidDate = (day, month, year) => {
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === parseInt(year) && 
           date.getMonth() === month - 1 && 
           date.getDate() === parseInt(day);
  };

  // Update display value when prop changes
  useEffect(() => {
    setDisplayValue(formatToDisplay(value));
  }, [value]);

  const handleManualChange = (e) => {
    let rawValue = e.target.value;
    
    // Allow only numbers and slashes
    rawValue = rawValue.replace(/[^\d/]/g, "");
    
    // Auto-format as user types
    if (rawValue.length === 2 && !rawValue.includes("/")) {
      rawValue = rawValue + "/";
    } else if (rawValue.length === 5 && rawValue.split("/").length === 2 && !rawValue.endsWith("/")) {
      rawValue = rawValue + "/";
    }
    
    // Limit to 10 characters (DD/MM/YYYY)
    if (rawValue.length <= 10) {
      setDisplayValue(rawValue);
      
      // Auto-update the actual value when complete
      if (rawValue.length === 10 && rawValue.includes("/")) {
        const [day, month, year] = rawValue.split("/");
        if (day && month && year && day.length === 2 && month.length === 2 && year.length === 4) {
          if (isValidDate(parseInt(day), parseInt(month), parseInt(year))) {
            const storageValue = formatToStorage(rawValue);
            
            // Create synthetic event
            const syntheticEvent = {
              target: {
                name: name,
                value: storageValue
              }
            };
            onChange(syntheticEvent);
          }
        }
      }
    }
  };

  const handleManualBlur = () => {
    // Validate and format on blur
    if (displayValue.length === 10 && displayValue.includes("/")) {
      const [day, month, year] = displayValue.split("/");
      if (day && month && year && day.length === 2 && month.length === 2 && year.length === 4) {
        if (isValidDate(parseInt(day), parseInt(month), parseInt(year))) {
          const storageValue = formatToStorage(displayValue);
          const syntheticEvent = {
            target: {
              name: name,
              value: storageValue
            }
          };
          onChange(syntheticEvent);
          return;
        }
      }
    }
    
    // If invalid, clear or show error (optional)
    if (displayValue && displayValue.length > 0) {
      // Optionally show error or revert to previous value
      setDisplayValue(formatToDisplay(value));
    }
  };

  const handleCalendarChange = (e) => {
    const newValue = e.target.value;
    setDisplayValue(formatToDisplay(newValue));
    onChange(e);
  };

  const handleIconClick = () => {
    if (dateInputRef.current) {
      dateInputRef.current.showPicker();
    }
  };

  return (
    <div className={`${styles.inputWrap} ${isError ? styles.inputError : ""}`} style={{ position: "relative" }}>
      <input
        type="text"
        placeholder="DD/MM/YYYY"
        value={displayValue}
        onChange={handleManualChange}
        onBlur={handleManualBlur}
        style={{ cursor: "text", paddingRight: "30px" }}
      />
      <button
        type="button"
        onClick={handleIconClick}
        style={{
          position: "absolute",
          right: "8px",
          top: "50%",
          transform: "translateY(-50%)",
          background: "none",
          border: "none",
          cursor: "pointer",
          fontSize: "16px",
          padding: "0 4px"
        }}
      >
        📅
      </button>
      <input
        ref={dateInputRef}
        name={name}
        type="date"
        value={value || ""}
        max={new Date().toISOString().split("T")[0]}
        onChange={handleCalendarChange}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          cursor: "pointer",
          pointerEvents: "none"
        }}
      />
    </div>
  );
}

export default function Form1({ onClose, id, recordType, onSubmit, registerValidation, onValidationChange }) {
  const [formData, setFormData] = useState({
    name: "", initial: "", gender: "Male", email: "", dateOfBirth: "", age: "",
    mobile: "", whatsapp: "", currentStayLocation: "", relationName: "",
    emergencyNumber: "", positionApplying: "", preferredCities: [], preferredAreas: []
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [positionOptions, setPositionOptions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [touched, setTouched] = useState({});
  const [cityOptions, setCityOptions] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [areaOptions, setAreaOptions] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(false);

  // --- FIX: stable refs, never recreated ---
  const validateRef = useRef(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData; // always up-to-date without deps

  useEffect(() => { fetchJobRoles(); fetchCities(); }, []);
  useEffect(() => { if (id) fetchCandidateData(id); }, [id, recordType]);

  const fetchJobRoles = async () => {
    setLoadingPositions(true);
    try {
      const response = await getJobRole(1);
      if (response?.statuscode === 200 && response.results) {
        setPositionOptions(response.results.map(item => item.jobRoles));
      }
    } catch (error) { console.error("Error fetching job roles:", error); }
    finally { setLoadingPositions(false); }
  };

  const fetchCities = async () => {
    setLoadingCities(true);
    try {
      const response = await GetPreferredCities();
      if (response?.status === "success" && response.data) setCityOptions(response.data);
    } catch (error) { console.error("Error fetching cities:", error); }
    finally { setLoadingCities(false); }
  };

  const fetchAreas = async (cityId) => {
    setLoadingAreas(true);
    try {
      const response = await GetPrefferedAreaList(cityId);
      let areasList = [];
      if (Array.isArray(response)) areasList = response.map(item => item.areas);
      else if (response?.data) areasList = response.data.map(item => item.areas);
      else if (response?.results) areasList = response.results.map(item => item.areas);

      setAreaOptions(prev => {
        const newAreas = [...prev];
        areasList.forEach(area => { if (!newAreas.includes(area)) newAreas.push(area); });
        return newAreas;
      });
    } catch (error) { console.error("Error fetching areas:", error); }
    finally { setLoadingAreas(false); }
  };

  const fetchCandidateData = async (recordId) => {
    setLoading(true);
    try {
      const response = await Getcanleaddata(recordId, recordType);
      if (response?.code === 200) {
        const candidate = response.data;

        let preferredCitiesArray = [];
        if (candidate.preferred_cities && Array.isArray(candidate.preferred_cities)) {
          preferredCitiesArray = candidate.preferred_cities;
        } else if (candidate.pref_location && typeof candidate.pref_location === 'string') {
          preferredCitiesArray = candidate.pref_location.split(',').map((n, i) => ({ id: i + 1, city: n.trim() }));
        }

        let preferredAreasArray = [];
        if (candidate.pref_area) {
          if (Array.isArray(candidate.pref_area)) preferredAreasArray = candidate.pref_area;
          else if (typeof candidate.pref_area === 'string') preferredAreasArray = candidate.pref_area.split(',');
        }

        setFormData({
          name: recordType === 'candidate' ? (candidate.first_name || "") : (candidate.name || ""),
          initial: candidate.last_name || "",
          gender: candidate.gender || "Male",
          email: candidate.email_id || "",
          dateOfBirth: candidate.date_of_birth || "",
          age: candidate.age ? candidate.age.toString() : "",
          mobile: candidate.mobile_number || "",
          whatsapp: candidate.whatsapp_number || "",
          currentStayLocation: candidate.city || "",
          relationName: candidate.relation_name || "",
          emergencyNumber: candidate.emergency_contact_number || "",
          positionApplying: candidate.job_category || "",
          preferredCities: preferredCitiesArray,
          preferredAreas: preferredAreasArray,
        });
      }
    } catch (error) { console.error("Error fetching candidate data:", error); }
    finally { setLoading(false); }
  };

  const safeTrim = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  };

  // validate reads from formDataRef so it doesn't need formData as dep
  const validate = useCallback((silent = false) => {
    const fd = formDataRef.current;
    const newErrors = {};

    const requiredFields = ["name", "initial", "dateOfBirth", "mobile", "whatsapp", "currentStayLocation", "positionApplying"];
    requiredFields.forEach(field => {
      const val = safeTrim(fd[field]);
      switch (field) {
        case "name": if (!val) newErrors.name = "Name is required"; break;
        case "initial": if (!val) newErrors.initial = "Initial is required"; break;
        case "dateOfBirth": {
          if (!val) { newErrors.dateOfBirth = "Date of birth is required"; break; }
          const birthDate = new Date(val);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const m = today.getMonth() - birthDate.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
          if (age < 18) newErrors.dateOfBirth = "Age must be at least 18 years";
          else if (age > 70) newErrors.dateOfBirth = "Age must be less than 70 years";
          break;
        }
        case "mobile":
          if (!val) newErrors.mobile = "Mobile number is required";
          else if (val.length !== 10) newErrors.mobile = "Enter valid 10-digit mobile number";
          break;
        case "whatsapp":
          if (!val) newErrors.whatsapp = "Whatsapp number is required";
          else if (val.length !== 10) newErrors.whatsapp = "Enter valid 10-digit whatsapp number";
          break;
        case "currentStayLocation": if (!val) newErrors.currentStayLocation = "Current stay location is required"; break;
        case "positionApplying": if (!val) newErrors.positionApplying = "Position Applying is required"; break;
        default: break;
      }
    });

    if (fd.email && safeTrim(fd.email)) {
      const emailRegex = /^[^\s@]+@([^\s@.,]+\.)+[^\s@.,]{2,}$/;
      if (!emailRegex.test(safeTrim(fd.email))) newErrors.email = "Enter a valid email address";
    }

    if (fd.emergencyNumber && safeTrim(fd.emergencyNumber)) {
      const en = safeTrim(fd.emergencyNumber);
      if (en.length !== 10) newErrors.emergencyNumber = "Enter valid 10-digit number";
    }

    if (fd.preferredCities.length === 0) newErrors.preferredCities = "At least one preferred city is required";

    if (!silent) setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  }, []); // no deps - reads from ref

  // Keep validateRef current
  validateRef.current = validate;

  // Register validation ONCE on mount
  useEffect(() => {
    if (registerValidation) {
      registerValidation((silent = false) => validateRef.current(silent));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buildApiData = () => {
    const fd = formDataRef.current;
    return {
      assign_to: decryptedAdminID(),
      name: fd.name,
      last_name: fd.initial,
      gender: fd.gender && fd.gender.trim() !== "" ? fd.gender : "Male",
      email_id: fd.email || "",
      mobile_number: fd.mobile,
      whatsapp_number: fd.whatsapp,
      date_of_birth: fd.dateOfBirth,
      age: calculateAge(fd.dateOfBirth),
      pref_area: fd.preferredAreas.join(','),
      pref_location: fd.preferredCities.map(city => city.city).join(','),
      city: typeof fd.currentStayLocation === 'string' ? fd.currentStayLocation : "",
      job_category: fd.positionApplying,
      relation_name: fd.relationName || "",
      emergency_contact_number: fd.emergencyNumber || "",
    };
  };

  const prevIsValidRef = useRef(true);
  useEffect(() => {
    if (onValidationChange) {
      const isValid = Object.keys(errors).length === 0;
      if (prevIsValidRef.current !== isValid) {
        prevIsValidRef.current = isValid;
        onValidationChange();
      }
    }
  }, [errors, onValidationChange]);

  const handleSelect = useCallback((field) => (val) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setFormData((prev) => ({ ...prev, [field]: val || "" }));
    if (val) setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const handleCitySelect = useCallback((city) => {
    setTouched(prev => ({ ...prev, preferredCities: true }));
    setFormData(prev => ({ ...prev, preferredCities: [...prev.preferredCities, city] }));
    fetchAreas(city.id);
    setErrors(prev => ({ ...prev, preferredCities: "" }));
  }, []);

  const handleCityRemove = useCallback((cityId) => {
    setTouched(prev => ({ ...prev, preferredCities: true }));
    setFormData(prev => ({ ...prev, preferredCities: prev.preferredCities.filter(c => c.id !== cityId) }));
  }, []);

  const handleAreaSelect = useCallback((area) => {
    setTouched(prev => ({ ...prev, preferredAreas: true }));
    setFormData(prev => ({ ...prev, preferredAreas: [...prev.preferredAreas, area] }));
    setErrors(prev => ({ ...prev, preferredAreas: "" }));
  }, []);

  const handleAreaRemove = useCallback((area) => {
    setTouched(prev => ({ ...prev, preferredAreas: true }));
    setFormData(prev => ({ ...prev, preferredAreas: prev.preferredAreas.filter(a => a !== area) }));
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setTouched(prev => ({ ...prev, [name]: true }));

    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [name]: value,
      };

      // Calculate and set age when date of birth changes
      if (name === "dateOfBirth") {
        const calculatedAge = calculateAge(value);
        updatedData.age = calculatedAge !== null ? calculatedAge.toString() : "";
      }

      return updatedData;
    });

    setErrors((prev) => ({ ...prev, [name]: "" }));
  }, []);

  const handleBlur = useCallback((e) => {
    const { name, value } = e.target;
    // Run validate silently to update errors for this field only
    setTimeout(() => {
      const result = validateRef.current(true);
      if (!result.isValid && result.errors[name]) {
        setErrors(prev => ({ ...prev, [name]: result.errors[name] }));
      }
    }, 0);
  }, []);

  const handleSubmit = useCallback(async () => {
    const validation = validateRef.current(false);
    setErrors(validation.errors);
    if (!validation.isValid) return false;

    setLoading(true);
    try {
      const apiData = buildApiData();
      const response = await PostRegister(id, 1, apiData, recordType);
      if (response?.code === 200) {
        if (onSubmit) onSubmit(apiData, false);
        return true;
      } else {
        setErrors({ submit: response?.message || "Failed to save data" });
        return false;
      }
    } catch (error) {
      console.error("Error saving form1:", error);
      setErrors({ submit: "Network error. Please try again." });
      return false;
    } finally {
      setLoading(false);
    }
  }, [id, recordType, onSubmit]);

  if (loading) {
    return (
      <div className={styles.formContainer}>
        <div className={styles.formContent}>
          <div className={styles.formTitle}>Loading Candidate Data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.formContainer}>
      <div className={styles.formContent}>
        <div className={styles.formTitle}>Add Candidate Details</div>
        {errors.submit && <div className={styles.errorSummary}>{errors.submit}</div>}

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>Name <span className={styles.required}>*</span></label>
            <div className={`${styles.inputWrap} ${errors.name ? styles.inputError : ""}`}>
              <input name="name" placeholder="Enter Candidate Name" value={formData.name} onChange={handleChange} onBlur={handleBlur}
                onInput={(e) => { e.target.value = e.target.value.replace(/[^A-Za-z ]/g, ""); }} />
            </div>
            {errors.name && <span className={styles.errorMsg}>{errors.name}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Initial <span className={styles.required}>*</span></label>
            <div className={`${styles.inputWrap} ${errors.initial ? styles.inputError : ""}`}>
              <input name="initial" placeholder="Enter Initial" value={formData.initial} onChange={handleChange} onBlur={handleBlur} onInput={(e) => {
                e.target.value = e.target.value.replace(/[^A-Za-z.\s]/g, "");
              }} />
            </div>
            {errors.initial && <span className={styles.errorMsg}>{errors.initial}</span>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>Gender</label>
            <AutoSelect options={GENDER_OPTIONS} placeholder="Select Gender" value={formData.gender} onChange={handleSelect("gender")} isError={!!errors.gender} />
            {errors.gender && <span className={styles.errorMsg}>{errors.gender}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Email ID (Optional)</label>
            <div className={`${styles.inputWrap} ${errors.email ? styles.inputError : ""}`}>
              <input name="email" type="email" placeholder="Enter Email Address" value={formData.email} onChange={handleChange} onBlur={handleBlur} />
            </div>
            {errors.email && <span className={styles.errorMsg}>{errors.email}</span>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>Date of Birth <span className={styles.required}>*</span></label>
            <DateInput name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} isError={!!errors.dateOfBirth} />
            {errors.dateOfBirth && <span className={styles.errorMsg}>{errors.dateOfBirth}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Current stay location? <span className={styles.required}>*</span></label>
            <div className={`${styles.inputWrap} ${errors.currentStayLocation ? styles.inputError : ""}`}>
              <input name="currentStayLocation" placeholder="Enter Current Stay Location" value={formData.currentStayLocation} onChange={handleChange} onBlur={handleBlur} />
            </div>
            {errors.currentStayLocation && <span className={styles.errorMsg}>{errors.currentStayLocation}</span>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>Mobile Number <span className={styles.required}>*</span></label>
            <PhoneInput
              name="mobile"
              placeholder="Enter Mobile Number"
              value={formData.mobile}
              onChange={handleChange}
              isError={!!errors.mobile}
              disabled={true}
            />
            {errors.mobile && <span className={styles.errorMsg}>{errors.mobile}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Whatsapp Number <span className={styles.required}>*</span></label>
            <PhoneInput name="whatsapp" placeholder="Enter Whatsapp Number" value={formData.whatsapp} onChange={handleChange} isError={!!errors.whatsapp} />
            {errors.whatsapp && <span className={styles.errorMsg}>{errors.whatsapp}</span>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>Position Applying For <span className={styles.required}>*</span></label>
            <AutoSelect options={positionOptions} placeholder={loadingPositions ? "Loading positions..." : "Select position"} value={formData.positionApplying} onChange={handleSelect("positionApplying")} isError={!!errors.positionApplying} loading={loadingPositions} />
            {errors.positionApplying && <span className={styles.errorMsg}>{errors.positionApplying}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Preferred Cities <span className={styles.required}>*</span></label>
            <MultiCitySelect options={cityOptions} placeholder="Search and select cities..." selectedCities={formData.preferredCities} onSelect={handleCitySelect} onRemove={handleCityRemove} isError={!!errors.preferredCities} loading={loadingCities} />
            {errors.preferredCities && <span className={styles.errorMsg}>{errors.preferredCities}</span>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>Preferred Areas (Optional)</label>
            <MultiAreaSelect options={areaOptions} placeholder="Search and select areas..." selectedAreas={formData.preferredAreas} onSelect={handleAreaSelect} onRemove={handleAreaRemove} isError={!!errors.preferredAreas} loading={loadingAreas} disabled={formData.preferredCities.length === 0} />
            {errors.preferredAreas && <span className={styles.errorMsg}>{errors.preferredAreas}</span>}
            {formData.preferredCities.length === 0 && <span className={styles.infoMsg}>Please select preferred cities first</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Relation Name (Optional)</label>
            <div className={`${styles.inputWrap} ${errors.relationName ? styles.inputError : ""}`}>
              <input name="relationName" placeholder="Enter Relation Name" value={formData.relationName} onChange={handleChange} onBlur={handleBlur}
                onInput={(e) => { e.target.value = e.target.value.replace(/[^A-Za-z ]/g, ""); }} />
            </div>
            {errors.relationName && <span className={styles.errorMsg}>{errors.relationName}</span>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>Emergency Number (Optional)</label>
            <PhoneInput name="emergencyNumber" placeholder="Enter Emergency Number" value={formData.emergencyNumber} onChange={handleChange} isError={!!errors.emergencyNumber} />
            {errors.emergencyNumber && <span className={styles.errorMsg}>{errors.emergencyNumber}</span>}
          </div>
        </div>
      </div>

      <div className={styles.buttonWrapper}>
        <div className={styles.buttonGroup}>
          <button className={styles.btnCancel} onClick={onClose}>Cancel</button>
          <button className={styles.btnSave} onClick={handleSubmit}>
            <img src={saveIcon} alt="" width="16" height="16" />
            Save & Continue
          </button>
        </div>
      </div>
    </div>
  );
}